import { lazy } from 'react';
import i18next from 'i18next';
import permissions from '@shared/config/permissions';
import { AccountsIcon, DashboardIcon } from '@shared/ui/icons';
import { selectAccountByUuid } from 'redux_store/selectors/accountsSelectors';

// DS account edit visits a specific child by uuid. The earlier shortcut
// of using `metadata.currentAccount.businessName` was wrong — that's the
// DS user's own account, not the child being edited. Look the child up
// in `metadata.accounts[]` (same source the topbar switcher uses) and
// fall back to '' so the breadcrumb shows its shimmer skeleton until
// metadata catches up (only relevant on a deep-link reload).
const resolveDsAccountLabel = ({ params, state }) => {
  const account = selectAccountByUuid(state, params.accountUuid);
  return account?.businessName || '';
};

const AccountsList = lazy(() => import('../../../../../pages/accounts'));
// v3 Account Edit, replaces the legacy AccountEdit screen. Account
// creation now lives in the `@features/account-create` SidePanel,
// launched from the accounts table, so there is no longer a route
// pointing at the legacy AccountCreate screen.
const AccountEdit = lazy(() => import('../../../../../pages/account-edit'));
// Instant Assessments has its own dedicated page so the widget can
// use the full viewport — see `pages/instant-assessments/ui` for
// rationale. Accessed from the Account Edit rail but not nested
// inside its shell.
const InstantAssessments = lazy(() =>
  import('../../../../../pages/instant-assessments')
);
// One generic bulk-upload page serves all four upload routes (Claims /
// Patients / Insurance / Providers). `additional.uploadType` picks the
// per-domain config (endpoint slugs, validate-only support, response
// contract) — see pages/upload-batches/model/configs.js.
const UploadBatchesPage = lazy(() =>
  import('../../../../../pages/upload-batches')
);

const ACCOUNT_ROUTE_LIST_DS = {
  path: '/:currentAccountUuid/accounts',
  exact: true,
  additional: {
    component: AccountsList,
    protected: true,
    title: i18next.t('titles:accounts'),
    icon: AccountsIcon,
    permission: permissions.ACCOUNTS_GET,
    breadcrumb: {
      isRoot: true,
      label: ({ state }) => state.i18n?.en?.titles?.accounts || 'Accounts',
    },
  },
};

const ACCOUNT_ROUTE_EDIT_DS = {
  path: '/:currentAccountUuid/accounts/:accountUuid/edit',
  exact: true,
  parentRoute: ACCOUNT_ROUTE_LIST_DS,
  additional: {
    component: AccountEdit,
    protected: true,
    title: i18next.t('titles:editAccount'),
    breadcrumb: {
      isRoot: false,
      parentPath: '/:currentAccountUuid/accounts',
      label: resolveDsAccountLabel,
    },
  },
};

const ACCOUNT_ROUTE_PATIENT_UPLOAD = {
  path: '/:currentAccountUuid/accounts/:accountUuid/patientUpload',
  exact: true,
  parentRoute: ACCOUNT_ROUTE_LIST_DS,
  additional: {
    component: UploadBatchesPage,
    title: i18next.t('titles:patientUpload'),
    protected: true,
    icon: DashboardIcon,
    permission: permissions.ADMIN_PATIENT_IMPORT,
    uploadType: 'patient',
  },
};

const ACCOUNT_ROUTE_INSURANCE_UPLOAD = {
  path: '/:currentAccountUuid/accounts/:accountUuid/insuranceUpload',
  exact: true,
  parentRoute: ACCOUNT_ROUTE_LIST_DS,
  additional: {
    component: UploadBatchesPage,
    title: i18next.t('titles:insuranceUpload'),
    protected: true,
    icon: DashboardIcon,
    permission: permissions.ADMIN_PATIENT_IMPORT,
    uploadType: 'insurance',
  },
};

const ACCOUNT_ROUTE_CLAIMS_UPLOAD = {
  path: '/:currentAccountUuid/accounts/:accountUuid/claimsUpload',
  exact: true,
  parentRoute: ACCOUNT_ROUTE_LIST_DS,
  additional: {
    component: UploadBatchesPage,
    title: i18next.t('titles:claimsUpload'),
    protected: true,
    icon: DashboardIcon,
    permission: permissions.ADMIN_PATIENT_IMPORT,
    uploadType: 'claims',
  },
};

const ACCOUNT_ROUTE_PROVIDERS_UPLOAD = {
  path: '/:currentAccountUuid/accounts/:accountUuid/providersUpload',
  exact: true,
  parentRoute: ACCOUNT_ROUTE_LIST_DS,
  additional: {
    component: UploadBatchesPage,
    title: i18next.t('titles:providersUpload'),
    protected: true,
    icon: DashboardIcon,
    permission: permissions.ADMIN_PATIENT_IMPORT,
    uploadType: 'providers',
  },
};

const ACCOUNT_ROUTE_EDIT = {
  path: '/:currentAccountUuid/edit',
  exact: true,
  additional: {
    component: AccountEdit,
    protected: true,
    title: i18next.t('titles:editAccount'),
    breadcrumb: {
      isRoot: true,
      label: ({ state }) => state.i18n?.en?.titles?.editAccount || 'Edit Account',
    },
  },
};

const ACCOUNT_ROUTE_INSTANT_ASSESSMENTS_DS = {
  path: '/:currentAccountUuid/accounts/:accountUuid/instant-assessments',
  exact: true,
  parentRoute: ACCOUNT_ROUTE_EDIT_DS,
  additional: {
    component: InstantAssessments,
    protected: true,
    title: i18next.t('titles:instantAssessment'),
    breadcrumb: {
      isRoot: false,
      parentPath: '/:currentAccountUuid/accounts/:accountUuid/edit',
      label: ({ state }) => state.i18n?.en?.titles?.instantAssessment || 'Instant Assessments',
    },
  },
};

const ACCOUNT_ROUTE_INSTANT_ASSESSMENTS = {
  path: '/:currentAccountUuid/instant-assessments',
  exact: true,
  parentRoute: ACCOUNT_ROUTE_EDIT,
  additional: {
    component: InstantAssessments,
    protected: true,
    title: i18next.t('titles:instantAssessment'),
    breadcrumb: {
      isRoot: false,
      parentPath: '/:currentAccountUuid/edit',
      label: ({ state }) => state.i18n?.en?.titles?.instantAssessment || 'Instant Assessments',
    },
  },
};

export const ACCOUNT_ROUTES = {
  // ------- For child accounts ------
  listDS: ACCOUNT_ROUTE_LIST_DS,
  editDS: ACCOUNT_ROUTE_EDIT_DS,
  instantAssessmentsDS: ACCOUNT_ROUTE_INSTANT_ASSESSMENTS_DS,
  patientUpload: ACCOUNT_ROUTE_PATIENT_UPLOAD,
  insuranceUpload: ACCOUNT_ROUTE_INSURANCE_UPLOAD,
  claimsUpload: ACCOUNT_ROUTE_CLAIMS_UPLOAD,
  providersUpload: ACCOUNT_ROUTE_PROVIDERS_UPLOAD,

  // ------- For current accounts ------
  edit: ACCOUNT_ROUTE_EDIT,
  instantAssessments: ACCOUNT_ROUTE_INSTANT_ASSESSMENTS,
};
